import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Newspaper, ExternalLink, Quote } from "lucide-react";
import { getLangFromUrl, setLangInUrl, useHreflang, updateCanonical } from "@/utils/seoLang";
import { t } from "@/translations";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PATH = "/press-about-us";
const LANGS = ["ru", "en"];

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function PressCard({ item, page, lang }) {
  return (
    <article className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`press-item-${item.id}`}>
      {item.image && (
        <a href={item.url} target="_blank" rel="noopener" className="block aspect-[16/7] overflow-hidden bg-gray-100">
          <img src={item.image} alt={item.title} loading="lazy" className="w-full h-full object-cover" />
        </a>
      )}
      <div className="p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mb-3">
          <span className="inline-flex items-center gap-1.5 font-semibold text-gray-800" data-testid="press-item-outlet"><Newspaper className="w-4 h-4" />{item.outlet}</span>
          <span>·</span>
          <time dateTime={item.date}>{formatDate(item.date, lang)}</time>
          {item.author && (<><span>·</span><span>{item.author}</span></>)}
        </div>
        <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">
          <a href={item.url} target="_blank" rel="noopener" className="hover:underline" data-testid="press-item-title-link">{item.title}</a>
        </h3>
        <p className="text-gray-700 leading-relaxed mb-5">{item.summary}</p>
        {item.quotes?.length > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">{page.quotes_heading}</div>
            <ul className="space-y-3">
              {item.quotes.map((q, i) => (
                <li key={i} className="flex gap-3 text-gray-800 italic"><Quote className="w-4 h-4 mt-1 shrink-0 text-emerald-600" /><span>{q}</span></li>
              ))}
            </ul>
          </div>
        )}
        <a href={item.url} target="_blank" rel="noopener" className="inline-flex items-center gap-2 text-emerald-700 font-medium hover:underline" data-testid="press-item-read-link">
          {page.read} {item.outlet} <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </article>
  );
}

export default function PressPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, LANGS));
  const [data, setData] = useState(null);
  const setLang = (nl) => { setLangState(nl); setLangInUrl(nl, searchParams, setSearchParams); };
  useHreflang(PATH, LANGS);
  const cLang = lang === "en" ? "en" : "ru";

  useEffect(() => {
    axios.get(`${API}/press?lang=${cLang}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [cLang]);

  useEffect(() => {
    if (!data) return;
    const url = `https://yuccaflower.com${PATH}${lang !== "ru" ? `?lang=${lang}` : ""}`;
    document.title = data.page.title;
    const setMeta = (sel, attr, val) => { const el = document.querySelector(sel); if (el) el.setAttribute(attr, val); };
    setMeta('meta[name="description"]', "content", data.page.description);
    setMeta('meta[property="og:title"]', "content", data.page.title);
    setMeta('meta[property="og:description"]', "content", data.page.description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[name="twitter:title"]', "content", data.page.title);
    setMeta('meta[name="twitter:description"]', "content", data.page.description);
    updateCanonical(PATH, LANGS);
    document.documentElement.lang = lang;
  }, [data, lang]);

  const openWhatsApp = () => window.open("https://wa.me/905316224378", "_blank");
  const page = data?.page;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header lang={lang} onLangChange={setLang} t={(k, v) => t(k, lang, v)} solid />
      <main className="pt-24 pb-16" data-testid="press-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <nav className="text-sm text-gray-500 mb-6" aria-label="breadcrumb">
            <a href="/" className="hover:underline">YUCCA Flowers</a> / <span data-testid="press-breadcrumb">{cLang === "en" ? "Press" : "Пресса"}</span>
          </nav>
          {page && (
            <>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="press-h1">{page.h1}</h1>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-10 max-w-3xl">{page.intro}</p>

              <section className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 mb-12" data-testid="press-facts">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">{page.facts_heading}</h2>
                <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                  {data.facts.map((f) => (
                    <div key={f.label}>
                      <dt className="text-xs uppercase tracking-wide text-gray-400">{f.label}</dt>
                      <dd className="text-gray-800 mt-0.5">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-5">{page.items_heading}</h2>
              <div className="space-y-8 mb-14" data-testid="press-items">
                {data.items.map((it) => <PressCard key={it.id} item={it} page={page} lang={cLang} />)}
              </div>

              <section className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 sm:p-8" data-testid="press-contact">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2">{page.press_contact_heading}</h2>
                <p className="text-gray-700 mb-4">{page.press_contact}</p>
                <div className="flex flex-wrap gap-3">
                  <button onClick={openWhatsApp} className="px-5 py-2.5 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors" data-testid="press-whatsapp-btn">WhatsApp</button>
                  <a href="https://t.me/Yucca_flower" target="_blank" rel="noopener" className="px-5 py-2.5 rounded-full border border-gray-300 text-gray-800 font-medium hover:bg-white transition-colors" data-testid="press-telegram-link">Telegram</a>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
      <Footer lang={lang} t={(k, v) => t(k, lang, v)} />
      <FloatingWhatsApp onClick={openWhatsApp} />
    </div>
  );
}
